import { Box } from "@mui/material";

/**
 * Skip navigation link for keyboard users
 * Allows users to skip to main content
 * WCAG 2.4.1: Must be the first focusable element and visible when focused
 */
export const SkipLink = () => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const mainContent = document.getElementById("main-content");
    if (mainContent) {
      mainContent.focus();
      mainContent.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <Box
      component="a"
      href="#main-content"
      onClick={handleClick}
      sx={(theme) => ({
        // Position off-screen initially but keep in DOM
        position: "fixed",
        top: "-100px",
        left: "6px",
        // Make it the first focusable element (highest z-index)
        zIndex: 10000,
        // Styling
        backgroundColor: "primary.main",
        color: "common.white",
        padding: "12px 24px",
        textDecoration: "none",
        borderRadius: "4px",
        fontSize: "1rem",
        fontWeight: 600,
        lineHeight: "1.5",
        // Hide visually but keep in DOM for screen readers
        clip: "rect(0, 0, 0, 0)",
        clipPath: "inset(50%)",
        width: "1px",
        height: "1px",
        overflow: "hidden",
        // Show when focused (keyboard navigation) - MUST be visible
        "&:focus": {
          top: "6px",
          left: "6px",
          clip: "auto",
          clipPath: "none",
          width: "auto",
          height: "auto",
          overflow: "visible",
          outline: `3px solid ${theme.palette.mobile.green[500]}`,
          outlineOffset: "2px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        },
        // Ensure it's always focusable and visible on focus
        "&:focus-visible": {
          top: "6px",
          left: "6px",
          clip: "auto",
          clipPath: "none",
          width: "auto",
          height: "auto",
          overflow: "visible",
          outline: `3px solid ${theme.palette.mobile.green[500]}`,
          outlineOffset: "2px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
        },
        // Hover state for mouse users
        "&:hover": {
          backgroundColor: "primary.dark",
        },
      })}
      aria-label="Skip to main content"
    >
      Skip to main content
    </Box>
  );
};
